import { getProductDetails } from '@/features/shared/utils/getProductDetails'
import { type ProductCardProduct } from '@/features/shop/components/ProductCard/types'
import { useIsVisitorOrZipOnly } from '@/features/account/services/User/hooks'
import { Body } from '@shipt/design-system-typography'
import { Row } from '@shipt/design-system-layouts'
import { useLoyaltyPrice } from '@/features/shop/hooks/useLoyaltyPrice'
import { formatPrice } from '@/features/shared/utils/formatter'

interface Props {
  product: ProductCardProduct
}

export const ProductCardPrice = ({ product }: Props) => {
  const { onSale, pricing } = product
  const { formattedPrice, formattedSalePrice } = getProductDetails(product)
  const isVisitor = useIsVisitorOrZipOnly()

  if (onSale) {
    return <SalePrice formattedPrice={formattedPrice} formattedSalePrice={formattedSalePrice} />
  }

  if (!isVisitor && pricing?.loyalty.price) {
    return <LoyaltyPrice product={product} formattedPrice={formattedPrice} />
  }

  return <Body strong>{formattedPrice}</Body>
}

type LoyaltyPriceProps = Props & {
  formattedPrice: string
}

const LoyaltyPrice = ({ product, formattedPrice }: LoyaltyPriceProps) => {
  const { original_price, price } = product
  const { shouldShowLoyaltyPrice, loyaltyPrice } = useLoyaltyPrice(product)

  if (shouldShowLoyaltyPrice) {
    return (
      <SalePrice
        formattedSalePrice={formatPrice(loyaltyPrice)}
        formattedPrice={formatPrice(original_price || price)}
      />
    )
  }

  return <Body strong>{formattedPrice}</Body>
}

const SalePrice = ({
  formattedPrice,
  formattedSalePrice,
}: {
  formattedPrice: string
  formattedSalePrice: string
}) => (
  <Row spacing="xs" align="baseline">
    <Body concept="feedback" feedbackType="promotion" strong>
      {formattedSalePrice}
    </Body>
    <Body variant="secondary" size="sm" strike>
      {formattedPrice}
    </Body>
  </Row>
)
